import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import { brainTokens } from "../../../design/brainTheme";
import { Icon } from "../../../mui";

const c = brainTokens.color;

export const PET_PRESETS = [
  { id: "fox", label: "Fox", emoji: "🦊" },
  { id: "owl", label: "Owl", emoji: "🦉" },
  { id: "cat", label: "Cat", emoji: "🐱" },
  { id: "panda", label: "Panda", emoji: "🐼" },
  { id: "otter", label: "Otter", emoji: "🦦" },
  { id: "bird", label: "Bird", emoji: "🐦" },
  { id: "frog", label: "Frog", emoji: "🐸" },
  { id: "bee", label: "Bee", emoji: "🐝" },
] as const;

const SURPRISE_NAMES = ["Pip", "Moss", "Nudge", "Pixel", "Biscuit", "Echo", "Mochi", "Drift", "Pebble", "Wisp"];

export type PetMood = "well-fed" | "peckish" | "hungry";

export function petMood(weekCount: number): PetMood {
  if (weekCount >= 10) return "well-fed";
  if (weekCount >= 1) return "peckish";
  return "hungry";
}

export function moodCopy(mood: PetMood): string {
  if (mood === "well-fed") return "Well fed — capture has been generous this week.";
  if (mood === "peckish") return "Peckish — a few thoughts keep them going.";
  return "Hungry — quiet week; drop a thought when you can.";
}

export function PetCard({
  pet,
  stats,
  canConfigure,
  onConfigure,
}: {
  pet?: { name?: string; avatar?: string; persona?: string } | null;
  stats?: { week?: number; quarter?: number; projectsTouched?: number; favouriteTopic?: string | null };
  canConfigure?: boolean;
  onConfigure?: () => void;
}) {
  const week = stats?.week ?? 0;
  const mood = petMood(week);
  const preset = PET_PRESETS.find((p) => p.id === pet?.avatar) ?? PET_PRESETS[0];

  if (!pet?.name) {
    return (
      <Box sx={{ border: `1px dashed ${c.border}`, borderRadius: `${brainTokens.radius.panel}px`, p: 2.5 }}>
        <Box sx={{ fontSize: 14, fontWeight: 600, color: c.ink, mb: 0.5 }}>AI companion</Box>
        <Box sx={{ fontSize: 13, color: c.muted2, mb: 1.5 }}>
          Give your AI a name and a face — they never write or post on their own.
        </Box>
        {canConfigure && (
          <Button size="small" variant="outlined" onClick={onConfigure}>
            Meet your pet
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: `1px solid ${c.border}`,
        borderRadius: `${brainTokens.radius.panel}px`,
        p: 2.5,
        background: c.pageBg,
      }}
    >
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
        <Box sx={{ fontSize: 36, lineHeight: 1 }}>{preset?.emoji}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: 16, fontWeight: 650, color: c.ink }}>{pet.name}</Box>
          <Box sx={{ fontSize: 13, color: c.ink3, mt: 0.5 }}>{pet.persona || "A quiet companion."}</Box>
          <Box sx={{ fontSize: 12.5, color: c.muted2, mt: 1 }}>{moodCopy(mood)}</Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.25, fontSize: 12, color: c.muted2 }}>
            <Chip size="small" label={`${week} this week`} />
            <Chip size="small" label={`${stats?.quarter ?? 0} this quarter`} />
            {(stats?.projectsTouched ?? 0) > 0 && <Chip size="small" label={`${stats?.projectsTouched} projects`} />}
          </Box>
        </Box>
        {canConfigure && (
          <Button size="small" onClick={onConfigure} startIcon={<Icon name="settings" size={14} />}>
            Configure
          </Button>
        )}
      </Box>
    </Box>
  );
}

export function PetConfigurator({
  open,
  initial,
  onClose,
  onSave,
}: {
  open: boolean;
  initial?: { name?: string; avatar?: string; persona?: string } | null;
  onClose: () => void;
  onSave: (pet: { name: string; avatar: string; persona: string }) => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [avatar, setAvatar] = useState(initial?.avatar ?? "fox");
  const [persona, setPersona] = useState(initial?.persona ?? "");
  const preset = useMemo(() => PET_PRESETS.find((p) => p.id === avatar) ?? PET_PRESETS[0], [avatar]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Configure your AI pet</DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Box sx={{ fontSize: 48 }}>{preset?.emoji}</Box>
          <Box sx={{ fontWeight: 650, color: c.ink }}>{name || "…"}</Box>
          <Box sx={{ fontSize: 13, color: c.muted2 }}>{persona || "One-line persona"}</Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <TextField fullWidth label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <Button
            onClick={() => setName(SURPRISE_NAMES[Math.floor(Math.random() * SURPRISE_NAMES.length)]!)}
          >
            Surprise me
          </Button>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, mb: 2 }}>
          {PET_PRESETS.map((p) => (
            <Box
              key={p.id}
              onClick={() => setAvatar(p.id)}
              sx={{
                border: `1px solid ${avatar === p.id ? c.teal : c.border}`,
                background: avatar === p.id ? c.tealTint2 : c.pageBg,
                borderRadius: 1,
                p: 1.25,
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              <Box sx={{ fontSize: 28 }}>{p.emoji}</Box>
              <Box sx={{ fontSize: 11, color: c.muted2 }}>{p.label}</Box>
            </Box>
          ))}
        </Box>
        <TextField
          fullWidth
          label="Persona (one line)"
          value={persona}
          onChange={(e) => setPersona(e.target.value)}
          helperText="Your pet never writes or posts on its own."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim()}
          onClick={() => onSave({ name: name.trim(), avatar, persona: persona.trim() })}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
